<template>
  <section class="section-base form-section-base">
    <div class="form-section-base__title">Сведения о должнике (заявителе)</div>
    <v-container>
      <div class="form-section-base__controls">
        <v-row>
          <v-col>
            <v-radio-group v-model="role" row>
              <v-radio
                label="Физическое лицо"
                :value="Role.PERSON"
                :disabled="roleType !== Role.PERSON"
              />
              <v-radio
                label="Юридическое лицо"
                :value="Role.ORGANIZATION"
                :disabled="roleType !== Role.ORGANIZATION"
              />
              <v-radio
                label="Иностранное юридическое лицо"
                :value="Role.FOREIGN_ORGANIZATION"
                :disabled="
                  ![Role.FOREIGN_ORGANIZATION, Role.ORGANIZATION].includes(
                    roleType
                  )
                "
              />
            </v-radio-group>
          </v-col>
        </v-row>
      </div>

      <ManualPerson v-if="role === Role.PERSON" :v="$v.person" />

      <ManualOrganization
        v-if="role === Role.ORGANIZATION"
        :v="$v.organization"
      />

      <ManualForeignOrganization
        v-if="role === Role.FOREIGN_ORGANIZATION"
        :v="$v.foreignOrganization"
      />

      <Footer
        @next-btn-click="btnSaveClickHandler(StepDirection.NEXT)"
        @prev-btn-click="btnSaveClickHandler(StepDirection.PREV)"
        :first-step="isFirstStep"
        :loading-next="loadingNext"
      />
    </v-container>
  </section>
</template>

<script>
import cloneDeep from 'clone-deep';
import deepmerge from 'deepmerge';

import { Role } from '@/utils/dictionaries';
import { checkInvalid, leaveStep } from '@/utils/services';
import Footer from '@/components/services/elements/Footer';
import { ServicesModule } from '@/store/modules/services';
import { AuthModule } from '@/store/modules/auth';
import { trusteeModel } from '@/components/services/elements/TrusteeView';
import { StepDirection, StepName } from '@/types/services';
import ManualPerson, {
  manualPersonModel,
  manualPersonValidation,
} from './ManualPerson';
import ManualOrganization, {
  manualOrganizationModel,
  getManualOrganizationValidation,
} from './ManualOrganization';
import ManualForeignOrganization, {
  manualForeignOrganizationModel,
  getManualForeignOrganizationValidation,
} from './ManualForeignOrganization';
import { AcceptanceDepositStep } from '@/constants';

export default {
  name: 'Manual',
  props: {
    loadingNext: {
      type: Boolean,
      required: true,
    },
    roleType: {
      type: String,
      required: true,
    },
  },
  components: {
    ManualPerson,
    ManualOrganization,
    ManualForeignOrganization,
    Footer,
  },
  data() {
    return {
      StepDirection,
      StepName,
      leaveStep,
      Role,
      invalidFields: [],
      role: this.roleType,
      person: cloneDeep(manualPersonModel),
      organization: cloneDeep(manualOrganizationModel),
      foreignOrganization: cloneDeep(manualForeignOrganizationModel),
    };
  },
  computed: {
    isFirstStep() {
      return AcceptanceDepositStep.DEBTOR === StepName.S1;
    },
  },
  validations() {
    return {
      person: manualPersonValidation,
      organization: getManualOrganizationValidation(this.organization),
      foreignOrganization: getManualForeignOrganizationValidation(
        this.foreignOrganization
      ),
    };
  },
  created() {
    this.prefillDraft();
  },
  methods: {
    prefillDraft() {
      const debtorStore = cloneDeep(ServicesModule.statement.subjects.debtor);

      if (
        !debtorStore.person &&
        !debtorStore.organization &&
        !debtorStore.foreignOrganization
      ) {
        this.prefillEsia();
        return;
      }

      const statementStore = cloneDeep(
        ServicesModule.statement.subjects.debtor
      );

      if (
        statementStore.trustee?.documents &&
        !statementStore.trustee.documents.statusDocument
      ) {
        statementStore.trustee.documents.statusDocument = cloneDeep(
          trusteeModel.documents.statusDocument
        );
      }

      if (statementStore.person) {
        this.role = Role.PERSON;
        this.person = deepmerge(manualPersonModel, statementStore.person);
      }

      if (statementStore.organization) {
        this.role = Role.ORGANIZATION;
        this.organization = deepmerge.all([
          manualOrganizationModel,
          statementStore.organization,
          {
            trustee: statementStore.trustee,
          },
        ]);
      }

      if (statementStore.foreignOrganization) {
        this.role = Role.FOREIGN_ORGANIZATION;
        this.foreignOrganization = deepmerge.all([
          manualForeignOrganizationModel,
          statementStore.foreignOrganization,
          {
            trustee: statementStore.trustee,
          },
        ]);
      }
    },

    prefillEsia() {
      if (this.roleType === Role.PERSON) {
        this.prefillEsiaPerson();
      } else if (this.roleType === Role.ORGANIZATION) {
        this.prefillEsiaOrganization();
        this.prefillEsiaForeignOrganization();
      }
    },

    prefillEsiaPerson() {
      const user = cloneDeep(AuthModule.user);
      this.person = deepmerge(this.person, {
        lastName: user.lastName || null,
        firstName: user.firstName || null,
        patronymic: user.middleName || null,
        snils: user.snils || null,
        inn: user.inn || null,
        email: (user.contactInfo && user.contactInfo.email) || null,
        phone: (user.contactInfo && user.contactInfo.mobilePhone) || null,
      });
    },

    async prefillEsiaOrganization() {
      const userDetails = cloneDeep(AuthModule.user);
      const orgDetails = cloneDeep(AuthModule.orgDetails);
      const addressOLG = await AuthModule.findByIdAddress(
        orgDetails.addrDetails.registerAddressInfo
      );

      this.organization = deepmerge(this.organization, {
        name: orgDetails.fullName || null,
        ogrn: orgDetails.ogrn || null,
        inn: orgDetails.inn || null,
        email: (orgDetails.cttsDetails && orgDetails.cttsDetails.email) || null,
        address: addressOLG,

        trustee: deepmerge(this.organization.trustee, {
          lastName: userDetails.lastName || null,
          firstName: userDetails.firstName || null,
          patronymic: userDetails.middleName || null,
          snils: userDetails.snils || null,
          inn: userDetails.inn || null,
          email:
            (userDetails.contactInfo && userDetails.contactInfo.email) || null,
          phone:
            (userDetails.contactInfo && userDetails.contactInfo.mobilePhone) ||
            null,
        }),
      });
    },

    async prefillEsiaForeignOrganization() {
      const userDetails = cloneDeep(AuthModule.user);
      const orgDetails = cloneDeep(AuthModule.orgDetails);

      const addressOLG = await AuthModule.findByIdAddress(
        orgDetails.addrDetails.registerAddressInfo
      );
      const addressOPS = await AuthModule.findByIdAddress(
        orgDetails.addrDetails.currentAddressInfo
      );

      this.foreignOrganization = deepmerge(this.foreignOrganization, {
        name: orgDetails.fullName || null,
        inn: orgDetails.inn || null,
        address: addressOLG || addressOPS,

        trustee: deepmerge(this.foreignOrganization.trustee, {
          lastName: userDetails.lastName || null,
          firstName: userDetails.firstName || null,
          patronymic: userDetails.middleName || null,
          snils: userDetails.snils || null,
          inn: userDetails.inn || null,
          email:
            (userDetails.contactInfo && userDetails.contactInfo.email) || null,
          phone:
            (userDetails.contactInfo && userDetails.contactInfo.mobilePhone) ||
            null,
        }),
      });
    },

    assemblyModel() {
      const debtor = {};

      debtor[this.role] = cloneDeep(this[this.role]);
      delete debtor[this.role].trustee;

      if (this.role !== Role.PERSON) {
        debtor.trustee = cloneDeep(this[this.role]).trustee;

        if (!this[this.role].trustee.documents.statusDocument.name) {
          debtor.trustee.documents.statusDocument = null;
        }
      }

      return debtor;
    },

    touchFields() {
      this.invalidFields = [];

      this.$v[this.role].$touch();
      checkInvalid(this.$v[this.role], this.invalidFields);
    },

    btnSaveClickHandler(direction) {
      leaveStep({
        stepThis: this,
        direction,
        step: AcceptanceDepositStep.DEBTOR,
      });
    },
  },
};
</script>
